import { useAssetsUploadAsset } from '@/api/WhisApi/generated/assets/assets';
import type { AssetBodyDto } from '@/api/WhisApi/generated/model';
import { AssetBodyDtoType } from '@/api/WhisApi/generated/model';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { isOfflineSubmissionError } from '@/screens/ReportCreation/hooks/offlineReportSubmissionQueue/useOfflineReportSubmissionQueue';
import type { UploadedDocument } from '@/types/formField.types';
import { showToast } from '@components/Toast/toast.service';
import { useCallback, useState } from 'react';
import { buildUploadFile } from '../utils/supportingDocumentsUpload.utils';

type UseSupportingDocumentsUploadResult = {
  isUploadingDocuments: boolean;
  uploadSupportingDocuments: (
    documents?: UploadedDocument[],
  ) => Promise<AssetBodyDto[] | undefined>;
};

export const useSupportingDocumentsUpload =
  (): UseSupportingDocumentsUploadResult => {
    const { t } = useAppTranslation();
    const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);
    const { mutateAsync: uploadAsset } = useAssetsUploadAsset({
      mutation: {
        networkMode: 'always',
        retry: false,
      },
    });

    const uploadSupportingDocuments = useCallback(
      async (
        documents?: UploadedDocument[],
      ): Promise<AssetBodyDto[] | undefined> => {
        if (!documents?.length) {
          return undefined;
        }

        setIsUploadingDocuments(true);

        try {
          const existingAssets = documents
            .filter((document) => Boolean(document.fileName))
            .map((document) => ({
              name: document.name,
              fileName: document.fileName as string,
              type: AssetBodyDtoType.supportingDocument,
            }));

          const newDocuments = documents.filter(
            (document) => !document.fileName,
          );

          const uploadedAssets = await Promise.all(
            newDocuments.map(async (document) => {
              const file = await buildUploadFile(document);
              const assetResponse = await uploadAsset({ data: { file } });

              return {
                name: assetResponse.name ?? document.name,
                fileName: assetResponse.fileName,
                type: AssetBodyDtoType.supportingDocument,
              };
            }),
          );

          const assets = [...existingAssets, ...uploadedAssets];
          return assets.length ? assets : undefined;
        } catch (error) {
          console.error('Failed to upload supporting documents', error);
          if (!isOfflineSubmissionError(error)) {
            showToast({
              type: 'error',
              title: t('reportCreation.toasts.uploadSupportingDocumentsError'),
            });
          }
          throw error;
        } finally {
          setIsUploadingDocuments(false);
        }
      },
      [t, uploadAsset],
    );

    return {
      isUploadingDocuments,
      uploadSupportingDocuments,
    };
  };
